/**
 * API Response Type Definitions for Insights Hub
 *
 * Comprehensive TypeScript interfaces for API response handling including:
 * - Standardized success/error response formats
 * - Pagination metadata structures
 * - Analytics response formats
 * - Enhanced error handling with detailed context
 *
 * Compatible with Vue.js 2 and backend API specifications
 */

import {
  IRecentQuestion,
  IPinnedQuestion,
  IQuestionExecutionStats,
  IPinnedQuestionsSummary,
  IQuestionCategoryStats,
} from './Question';
import {
  IQuestionSuggestion,
} from './Topic';
import {
  IBaseTopic,
  ITopicDiscovery,
  ITopicIntelligence,
  ITopicAnalytics,
} from './Topic';

// =============================================================================
// CORE RESPONSE INTERFACES
// =============================================================================

/**
 * Base response interface that all API responses extend
 */
export interface IBaseApiResponse {
  /** Indicates if the request was successful */
  success: boolean;

  /** Response timestamp */
  timestamp: string;

  /** Optional response message */
  message?: string;

  /** Optional metadata */
  meta?: IResponseMetadata;

  /** Optional warnings that don't prevent success */
  warnings?: string[];
}

/**
 * Response metadata interface
 */
export interface IResponseMetadata {
  /** Unique request identifier for tracking */
  request_id: string;

  /** API version used */
  api_version?: string;

  /** Response generation time in milliseconds */
  response_time_ms?: number;

  /** Server that processed the request */
  server_id?: string;

  /** Cache status information */
  cache_status?: ICacheStatus;

  /** Rate limiting information */
  rate_limit?: IRateLimitInfo;

  /** Additional metadata */
  [key: string]: any;
}

/**
 * Cache status information
 */
export interface ICacheStatus {
  /** Cache hit or miss */
  status: 'hit' | 'miss' | 'bypass' | 'error';

  /** Cache key used */
  cache_key?: string;

  /** Cache TTL in seconds */
  ttl_seconds?: number;

  /** When cache entry was created */
  cached_at?: string;

  /** Cache invalidation reason if applicable */
  invalidation_reason?: string;
}

/**
 * Rate limiting information
 */
export interface IRateLimitInfo {
  /** Maximum requests allowed in window */
  limit: number;

  /** Remaining requests in current window */
  remaining: number;

  /** When the rate limit window resets (Unix timestamp) */
  reset_at: number;

  /** Rate limit window in seconds */
  window_seconds: number;

  /** Time until reset in seconds */
  retry_after?: number;
}

/**
 * Successful response interface
 */
export interface ISuccessResponse<T = any> extends IBaseApiResponse {
  success: true;
  data: T;
}

/**
 * Error response interface with detailed error information
 */
export interface IErrorResponse extends IBaseApiResponse {
  success: false;
  error: IApiError;
}

/**
 * API error interface with comprehensive error details
 */
export interface IApiError {
  /** Error category */
  category: ErrorCategory;

  /** Specific error code */
  code: string;

  /** Human-readable error message */
  message: string;

  /** Detailed error information */
  details?: IErrorDetail[];

  /** Field-specific errors for validation */
  field_errors?: Record<string, IFieldError[]>;

  /** Error timestamp */
  timestamp: string;

  /** Stack trace (only in development) */
  stack_trace?: string;

  /** Correlation ID for error tracking */
  correlation_id?: string;

  /** Suggested actions to resolve the error */
  suggested_actions?: string[];

  /** Documentation link for this error */
  docs_link?: string;
}

/**
 * Error categories matching backend error handling
 */
export enum ErrorCategory {
  VALIDATION_ERROR = 'validation_error',
  AUTHENTICATION_ERROR = 'authentication_error',
  AUTHORIZATION_ERROR = 'authorization_error',
  NOT_FOUND = 'not_found',
  CONFLICT = 'conflict',
  BUSINESS_LOGIC_ERROR = 'business_logic_error',
  RATE_LIMIT_EXCEEDED = 'rate_limit_exceeded',
  SERVER_ERROR = 'server_error',
  SERVICE_UNAVAILABLE = 'service_unavailable',
  TIMEOUT_ERROR = 'timeout_error',
  NETWORK_ERROR = 'network_error',
}

/**
 * Detailed error information
 */
export interface IErrorDetail {
  /** Field name related to the error */
  field?: string;

  /** Specific error code */
  code: string;

  /** Error message */
  message: string;

  /** Additional context */
  context?: Record<string, any>;

  /** Path to the problematic field (for nested objects) */
  field_path?: string;

  /** Expected value or format */
  expected?: any;

  /** Actual value that caused the error */
  actual?: any;
}

/**
 * Field-specific error information
 */
export interface IFieldError {
  /** Error code for this field */
  code: string;

  /** Error message for this field */
  message: string;

  /** Additional context */
  context?: Record<string, any>;
}

// =============================================================================
// PAGINATION INTERFACES
// =============================================================================

/**
 * Pagination metadata for paginated responses
 */
export interface IPaginationMetadata {
  /** Total number of records */
  total: number;

  /** Number of records per page */
  limit: number;

  /** Current offset */
  offset: number;

  /** Current page number (1-based) */
  page: number;

  /** Total number of pages */
  total_pages: number;

  /** Whether there are more records after current page */
  has_more: boolean;

  /** Human-readable showing information */
  showing: string;

  /** Next page offset (null if no more pages) */
  next_offset?: number;

  /** Previous page offset (null if first page) */
  prev_offset?: number;

  /** Current page information */
  current_page: {
    /** Number of records in current page */
    count: number;

    /** First record number in current page */
    start: number;

    /** Last record number in current page */
    end: number;
  };
}

/**
 * Paginated response interface
 */
export interface IPaginatedResponse<T = any> extends ISuccessResponse<T[]> {
  /** Pagination metadata */
  pagination: IPaginationMetadata;

  /** Applied filters */
  filters?: Record<string, any>;

  /** Applied sorting */
  sort?: {
    field: string;
    order: 'asc' | 'desc';
  };
}

// =============================================================================
// INSIGHTS HUB SPECIFIC RESPONSES
// =============================================================================

/**
 * Recent questions list response
 */
export interface IRecentQuestionsResponse extends IPaginatedResponse<IRecentQuestion> {
  data: IRecentQuestion[];

  /** Applied filters summary */
  filters_applied?: {
    category?: string;
    search?: string;
    is_pinned?: boolean;
    date_range?: {
      from: string;
      to: string;
    };
  };
}

/**
 * Single recent question response
 */
export interface IRecentQuestionResponse extends ISuccessResponse<IRecentQuestion> {
  data: IRecentQuestion;
}

/**
 * Pinned questions list response
 */
export interface IPinnedQuestionsResponse extends IBaseApiResponse {
  success: true;
  data: {
    /** Array of pinned questions */
    questions: IPinnedQuestion[];

    /** Pagination information */
    pagination: IPaginationMetadata;

    /** Quota information */
    quota: {
      used: number;
      limit: number;
      remaining: number;
    };
  };
}

/**
 * Single pinned question response
 */
export interface IPinnedQuestionResponse extends ISuccessResponse<IPinnedQuestion> {
  data: IPinnedQuestion & {
    /** Quota status after operation */
    quota_status: {
      used: number;
      limit: number;
      remaining: number;
    };
  };
}

/**
 * Question execution statistics response
 */
export interface IQuestionStatsResponse extends ISuccessResponse<IQuestionExecutionStats> {
  data: IQuestionExecutionStats;
}

/**
 * Pinned questions summary response
 */
export interface IPinnedSummaryResponse extends ISuccessResponse<IPinnedQuestionsSummary> {
  data: IPinnedQuestionsSummary;
}

/**
 * Question categories response
 */
export interface IQuestionCategoriesResponse extends IBaseApiResponse {
  success: true;
  data: {
    categories: IQuestionCategoryStats[];
    total_categories: number;
    total_questions: number;
  };
}

/**
 * Question suggestions response
 */
export interface IQuestionSuggestionsResponse extends IBaseApiResponse {
  success: true;
  data: {
    suggestions: IQuestionSuggestion[];
    metadata: {
      generation_time_ms: number;
      source: string;
      model?: string;
      context_used?: string[];
      total_generated: number;
    };
    fallback_suggestions?: IQuestionSuggestion[];
    fallback_info?: {
      reason: string;
      generated_count: number;
    };
  };
}

// =============================================================================
// INSIGHTS AND AI RESPONSES
// =============================================================================

/**
 * AI insight data interface
 */
export interface IInsightData {
  /** Insight type */
  type: 'trend_analysis' | 'anomaly_detection' | 'recommendation' | 'summary' | 'alert';

  /** Insight title */
  title: string;

  /** Insight content/description */
  content: string;

  /** Confidence score (0-1) */
  confidence: number;

  /** Number of data points analyzed */
  data_points: number;

  /** Structured recommendations */
  recommendations?: string[];

  /** Related visualizations */
  visualizations?: IVisualization[];

  /** Actionable insights */
  actions?: IInsightAction[];

  /** Data sources used */
  data_sources?: string[];
}

/**
 * Visualization interface for insights
 */
export interface IVisualization {
  /** Visualization type */
  type: 'bar_chart' | 'line_chart' | 'pie_chart' | 'table' | 'metric_card';

  /** Visualization title */
  title: string;

  /** Data URL or inline data */
  data_url?: string;
  data?: any;

  /** Chart configuration */
  config?: Record<string, any>;

  /** Description of what the visualization shows */
  description?: string;
}

/**
 * Insight action interface
 */
export interface IInsightAction {
  /** Action identifier */
  id: string;

  /** Action label for UI */
  label: string;

  /** Action type */
  type: 'filter' | 'navigate' | 'export' | 'create' | 'update' | 'analyze';

  /** Estimated impact description */
  estimated_impact?: string;

  /** URL for navigation actions */
  url?: string;

  /** Parameters for the action */
  params?: Record<string, any>;

  /** Priority level */
  priority?: 'high' | 'medium' | 'low';
}

/**
 * AI insights generation response
 */
export interface IInsightsResponse extends IBaseApiResponse {
  success: true;
  data: {
    /** Generated insights */
    insights: IInsightData[];

    /** Measure code used */
    measure_code: string;

    /** Dimension code used */
    dimension_code: string;

    /** Execution time in seconds */
    execution_time_seconds: number;

    /** When insights were generated */
    generated_at: string;

    /** Cache status */
    cache_status: string;

    /** Merchant/user identifier */
    merchant_id?: number;

    /** AI service status */
    ai_service_status?: string;
  };
}

/**
 * Individual question processing response
 */
export interface IQuestionProcessingResponse extends ISuccessResponse {
  data: {
    /** AI-generated answer */
    answer: string;

    /** Matched predefined question if any */
    matched_question?: string;

    /** Confidence in the answer */
    confidence: number;

    /** Data sources used */
    data_source: string;

    /** Execution time in seconds */
    execution_time: number;

    /** Related suggestions */
    suggestions?: string[];

    /** Visualizations for the answer */
    visualizations?: IVisualization[];

    /** Detected question category */
    detected_category?: string;

    /** Analysis type performed */
    analysis_type?: string;

    /** Additional data patterns found */
    data_patterns?: Array<{
      pattern_type: string;
      description: string;
      confidence: number;
    }>;
  };
}

// =============================================================================
// TOPIC RESPONSES
// =============================================================================

/**
 * Topics list response
 */
export interface ITopicsResponse extends IPaginatedResponse<IBaseTopic> {
  data: IBaseTopic[];
}

/**
 * Single topic response
 */
export interface ITopicResponse extends ISuccessResponse<IBaseTopic> {
  data: IBaseTopic;
}

/**
 * Topic discovery response
 */
export interface ITopicDiscoveryResponse extends IBaseApiResponse {
  success: true;
  data: {
    discovered_topics: ITopicDiscovery[];
    discovery_metadata: {
      search_time_ms: number;
      algorithms_used: string[];
      confidence_threshold: number;
    };
  };
}

/**
 * Topic intelligence response
 */
export interface ITopicIntelligenceResponse extends ISuccessResponse<ITopicIntelligence> {
  data: ITopicIntelligence;
}

/**
 * Topic analytics response
 */
export interface ITopicAnalyticsResponse extends ISuccessResponse<ITopicAnalytics> {
  data: ITopicAnalytics;
}

// =============================================================================
// BULK OPERATIONS RESPONSES
// =============================================================================

/**
 * Bulk operation result
 */
export interface IBulkOperationResult {
  /** Operation performed */
  operation: 'create' | 'update' | 'delete' | 'reorder';

  /** Total items processed */
  total_items: number;

  /** Successfully processed items */
  successful_items: number;

  /** Failed items */
  failed_items: number;

  /** Detailed results */
  results: Array<{
    /** Item identifier */
    item_id: string | number;

    /** Operation status */
    status: 'success' | 'error' | 'skipped';

    /** Error message if failed */
    error_message?: string;

    /** Error code if failed */
    error_code?: string;

    /** Updated data if successful */
    updated_data?: any;
  }>;

  /** Operation timestamp */
  processed_at: string;

  /** Processing time in seconds */
  processing_time_seconds: number;
}

/**
 * Bulk operation response
 */
export interface IBulkOperationResponse extends ISuccessResponse<IBulkOperationResult> {
  data: IBulkOperationResult;
}

// =============================================================================
// SYSTEM AND HEALTH RESPONSES
// =============================================================================

/**
 * System health status
 */
export interface ISystemHealth {
  /** Overall system status */
  status: 'healthy' | 'degraded' | 'unhealthy';

  /** Individual service statuses */
  services: {
    database: 'up' | 'down' | 'degraded';
    cache: 'up' | 'down' | 'degraded';
    ai_service: 'up' | 'down' | 'degraded';
    queue: 'up' | 'down' | 'degraded';
  };

  /** Performance metrics */
  metrics: {
    avg_response_time_ms: number;
    error_rate_percent: number;
    active_connections: number;
    memory_usage_percent: number;
    cpu_usage_percent: number;
  };

  /** Last health check timestamp */
  last_check_at: string;

  /** Health check version */
  version: string;
}

/**
 * System health response
 */
export interface IHealthResponse extends ISuccessResponse<ISystemHealth> {
  data: ISystemHealth;
}

// =============================================================================
// RESPONSE UTILITIES AND HELPERS
// =============================================================================

/**
 * Type guard to check if response is successful
 */
export function isSuccessResponse<T>(response: IBaseApiResponse): response is ISuccessResponse<T> {
  return response.success === true;
}

/**
 * Type guard to check if response is an error
 */
export function isErrorResponse(response: IBaseApiResponse): response is IErrorResponse {
  return response.success === false;
}

/**
 * Type guard to check if response is paginated
 */
export function isPaginatedResponse<T>(response: IBaseApiResponse): response is IPaginatedResponse<T> {
  return isSuccessResponse(response) && 'pagination' in response;
}

/**
 * Extract error message from API response
 */
export function extractErrorMessage(response: IErrorResponse): string {
  if (response.error.message) {
    return response.error.message;
  }

  if (response.error.details && response.error.details.length > 0) {
    return response.error.details[0].message;
  }

  return 'An unknown error occurred';
}

/**
 * Extract field errors from API response
 */
export function extractFieldErrors(response: IErrorResponse): Record<string, string[]> {
  const fieldErrors: Record<string, string[]> = {};

  if (response.error.field_errors) {
    Object.entries(response.error.field_errors).forEach(([field, errors]) => {
      fieldErrors[field] = errors.map((error) => error.message);
    });
  }

  return fieldErrors;
}

/**
 * Check if error is retryable based on error category
 */
export function isRetryableError(response: IErrorResponse): boolean {
  const retryableCategories = [
    ErrorCategory.RATE_LIMIT_EXCEEDED,
    ErrorCategory.SERVER_ERROR,
    ErrorCategory.SERVICE_UNAVAILABLE,
    ErrorCategory.TIMEOUT_ERROR,
    ErrorCategory.NETWORK_ERROR,
  ];

  return retryableCategories.includes(response.error.category);
}

/**
 * Get retry delay from rate limit information
 */
export function getRetryDelay(response: IErrorResponse): number | null {
  if (response.error.category === ErrorCategory.RATE_LIMIT_EXCEEDED) {
    return response.meta?.rate_limit?.retry_after || 60;
  }

  return null;
}

/**
 * Format pagination info for display
 */
export function formatPaginationInfo(pagination: IPaginationMetadata): string {
  return pagination.showing;
}

/**
 * Check if there are more pages available
 */
export function hasMorePages(pagination: IPaginationMetadata): boolean {
  return pagination.has_more;
}

/**
 * Get next page offset
 */
export function getNextPageOffset(pagination: IPaginationMetadata): number | null {
  return pagination.next_offset || null;
}

/**
 * Get previous page offset
 */
export function getPreviousPageOffset(pagination: IPaginationMetadata): number | null {
  return pagination.prev_offset || null;
}

// =============================================================================
// RESPONSE BUILDERS FOR TESTING AND MOCKING
// =============================================================================

/**
 * Create a mock success response
 */
export function createMockSuccessResponse<T>(data: T, meta?: Partial<IResponseMetadata>): ISuccessResponse<T> {
  return {
    success: true,
    data,
    timestamp: new Date().toISOString(),
    meta: {
      request_id: 'mock-request-id',
      response_time_ms: 100,
      ...meta,
    },
  };
}

/**
 * Create a mock error response
 */
export function createMockErrorResponse(
  category: ErrorCategory,
  code: string,
  message: string,
  details?: IErrorDetail[],
): IErrorResponse {
  return {
    success: false,
    timestamp: new Date().toISOString(),
    error: {
      category,
      code,
      message,
      details,
      timestamp: new Date().toISOString(),
    },
    meta: {
      request_id: 'mock-request-id',
      response_time_ms: 50,
    },
  };
}

/**
 * Create mock pagination metadata
 */
export function createMockPagination(
  total: number,
  limit: number = 20,
  offset: number = 0,
): IPaginationMetadata {
  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);
  const count = Math.min(limit, total - offset);

  return {
    total,
    limit,
    offset,
    page,
    total_pages: totalPages,
    has_more: offset + limit < total,
    showing: `Showing ${offset + 1}-${offset + count} of ${total} results`,
    next_offset: offset + limit < total ? offset + limit : undefined,
    prev_offset: offset > 0 ? Math.max(0, offset - limit) : undefined,
    current_page: {
      count,
      start: offset + 1,
      end: offset + count,
    },
  };
}
